import {Button} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useId} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useToggle} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {AdServer} from '../../../types/AdServer';
import {Collapse} from '../../Collapse/Collapse';
import {AdServerRow} from './AdServerRow';

export interface AdServerFilterSectionProps extends TestIdProps {
  adServers: AdServer[];
  server: AdServer[] | null | undefined;
  priceReportId: string;
  onServerSwitchChange: (adServer: AdServer, value: boolean) => void;
}

export function AdServerFilterSection(props: AdServerFilterSectionProps) {
  const secondaryAdServersContentId = useId();
  const secondaryAdServersToggleId = useId();
  const isMultiServers = props.adServers.length > 1;
  const primaryAdServer = props.adServers[0];
  const secondaryAdServers = props.adServers.slice(1);
  const singleSecondaryAdServer =
    secondaryAdServers.length === 1 ? secondaryAdServers[0] : undefined;
  const hasCollapsibleSecondaryAdServers = secondaryAdServers.length >= 2;
  const selectedCollapsibleAdServers = secondaryAdServers.filter((adServer) =>
    props.server?.includes(adServer)
  );
  const hasSelectedCollapsibleAdServers = selectedCollapsibleAdServers.length > 0;
  const [areMoreAdServersVisible, toggleMoreAdServersVisible] = useToggle(
    hasSelectedCollapsibleAdServers
  );

  const moreAdServersTitle =
    selectedCollapsibleAdServers.length > 0
      ? i18n.t('entity.filterForm.moreAdvertisementServersActive', {
          count: selectedCollapsibleAdServers.length,
        })
      : i18n.t('entity.filterForm.moreAdvertisementServers');

  return (
    <>
      {isNotNil(primaryAdServer) && (
        <AdServerRow
          adServer={primaryAdServer}
          value={!!props.server?.includes(primaryAdServer)}
          onChange={(value) => props.onServerSwitchChange(primaryAdServer, value)}
          showSwitch={isMultiServers}
          priceReportId={props.priceReportId}
        />
      )}
      {isNotNil(singleSecondaryAdServer) && (
        <AdServerRow
          adServer={singleSecondaryAdServer}
          value={!!props.server?.includes(singleSecondaryAdServer)}
          onChange={(value) => props.onServerSwitchChange(singleSecondaryAdServer, value)}
          showSwitch={isMultiServers}
          priceReportId={props.priceReportId}
        />
      )}
      {hasCollapsibleSecondaryAdServers && (
        <>
          {!areMoreAdServersVisible && (
            <Box id={secondaryAdServersToggleId}>
              <Button
                title={moreAdServersTitle}
                size="small"
                variant="ghostLink"
                rightIcon="navigation/expand_more"
                aria-expanded={false}
                aria-controls={secondaryAdServersContentId}
                onClick={toggleMoreAdServersVisible}
                data-testid={suffixTestId('showMoreAdServers', props)}
              />
            </Box>
          )}
          <Collapse
            isOpen={areMoreAdServersVisible}
            contentId={secondaryAdServersContentId}
            labelledById={secondaryAdServersToggleId}
          >
            <VStack spacing={4}>
              {secondaryAdServers.map((adServer) => (
                <AdServerRow
                  key={adServer}
                  adServer={adServer}
                  value={!!props.server?.includes(adServer)}
                  onChange={(value) => props.onServerSwitchChange(adServer, value)}
                  showSwitch={isMultiServers}
                  priceReportId={props.priceReportId}
                />
              ))}
              <Box id={secondaryAdServersToggleId}>
                <Button
                  title={i18n.t('entity.list.hide')}
                  size="small"
                  variant="ghostLink"
                  rightIcon="navigation/expand_less"
                  aria-expanded
                  aria-controls={secondaryAdServersContentId}
                  onClick={toggleMoreAdServersVisible}
                  data-testid={suffixTestId('hideMoreAdServers', props)}
                />
              </Box>
            </VStack>
          </Collapse>
        </>
      )}
    </>
  );
}
